// Libs
import classNames from 'classnames/bind';
import { Popover, Stack, Typography } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import moment from 'moment';
// Components, Layouts, Pages
import { NotificationItem } from '~/components/module/notification';
// Others
import { INotification } from '~/utils/interface/notification';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING, NEW_NOTIFICATION_THRESHOLD } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { readNotification } from '~/thunks/notification/notificationThunk';
import { notificationActions } from '~/thunks/notification/notificationSlice';
import { DateFormatEnum, NotificationStatusEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './NotificationList.module.scss';

type Props = {
  isOpen: boolean;
  anchorEl?: HTMLButtonElement | null;
  hasMore: boolean;
  onClose: () => void;
  onLoadMore: () => void;
};

const cx = classNames.bind(styles);

const NotificationList = (props: Props) => {
  //#region Destructuring Props
  const { isOpen, anchorEl, hasMore, onClose, onLoadMore } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/notification']);
  //#endregion Declare Hook

  //#region Selector
  const { notificationList } = useAppSelector((state) => state.notification);
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  const newNotification = useMemo(() => {
    const list = notificationList.filter((notificationItem: INotification) => {
      const createdAt = moment(notificationItem.createdAt);
      const diffInMinutes = moment().diff(moment(createdAt), 'minutes');
      const isToday = moment(createdAt.toDate()).isSame(moment(), 'day');

      return isToday && diffInMinutes < NEW_NOTIFICATION_THRESHOLD;
    });

    return list;
  }, [notificationList]);

  const todayNotification = useMemo(() => {
    const list = notificationList.filter((notificationItem: INotification) => {
      const createdAt = moment(notificationItem.createdAt);
      const diffInMinutes = moment().diff(moment(createdAt), 'minutes');
      const isToday = moment(createdAt.toDate()).isSame(moment(), 'day');

      return isToday && diffInMinutes >= NEW_NOTIFICATION_THRESHOLD;
    });

    return list;
  }, [notificationList]);

  const groupedOtherNotification = useMemo(() => {
    const grouped: Record<string, typeof notificationList> = {};

    notificationList.forEach((notificationItem) => {
      const createdAt = moment(notificationItem.createdAt);
      const formattedDate = moment(createdAt).format(DateFormatEnum.MM_DD_YYYY);
      const isToday = moment(createdAt.toDate()).isSame(moment(), 'day');

      if (isToday) return;

      if (!grouped[formattedDate]) {
        grouped[formattedDate] = [];
      }
      grouped[formattedDate].push(notificationItem);
    });

    return Object.entries(grouped).map(([date, notifications]) => ({ date, notifications }));
  }, [notificationList]);
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseNotification = () => {
    onClose();
  };

  const handleReadNotification = (notification: INotification) => {
    if (notification?.status === NotificationStatusEnum.UNREAD) {
      dispatch(readNotification(notification?.id))
        .unwrap()
        .then((res) => {
          dispatch(notificationActions.setIsRefreshNotification(true));
        })
        .catch((error) => {})
        .finally(() => {});
    }

    handleCloseNotification();
  };
  //#endregion Handle Function

  return (
    <Popover
      id={'notificationListComponent'}
      open={isOpen}
      anchorEl={anchorEl}
      onClose={handleCloseNotification}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{ paper: { className: cx('paper') } }}
    >
      <Stack component={'div'} id='notifications' spacing={'8px'} className={cx('notificationContainer')}>
        <InfiniteScroll
          dataLength={notificationList.length}
          next={onLoadMore}
          hasMore={hasMore}
          loader={
            // <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
            //   <CircularProgress size={LoadingSizeEnum.MEDIUM} />
            // </Stack>

            EMPTY_STRING
          }
          scrollThreshold={0.8}
          scrollableTarget={'notifications'}
        >
          <Stack gap={'20px'}>
            {notificationList?.length > DEFAULT_NUMBER_ZERO ? (
              <>
                {newNotification?.length > DEFAULT_NUMBER_ZERO && (
                  <Stack gap={'12px'}>
                    <Typography className={cx('notificationTitleGroup')}>New</Typography>

                    <Stack gap={'8px'}>
                      {newNotification?.map((notification) => (
                        <NotificationItem
                          key={notification?.id}
                          data={notification}
                          onClick={() => handleReadNotification(notification)}
                        />
                      ))}
                    </Stack>
                  </Stack>
                )}

                {todayNotification?.length > DEFAULT_NUMBER_ZERO && (
                  <Stack gap={'12px'}>
                    <Typography className={cx('notificationTitleGroup')}>Today</Typography>

                    <Stack gap={'8px'}>
                      {todayNotification?.map((notification) => (
                        <NotificationItem
                          key={notification?.id}
                          data={notification}
                          onClick={() => handleReadNotification(notification)}
                        />
                      ))}
                    </Stack>
                  </Stack>
                )}

                {groupedOtherNotification?.map(({ date, notifications }, index) => (
                  <Stack gap={'12px'} key={index}>
                    <Typography className={cx('notificationTitleGroup')}>{date}</Typography>

                    <Stack gap={'8px'}>
                      {notifications?.map((notification) => (
                        <NotificationItem
                          key={notification?.id}
                          data={notification}
                          onClick={() => handleReadNotification(notification)}
                        />
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </>
            ) : (
              <Typography component={'span'} className={cx('noDataAvailable')}>
                {t('common_no_data_available', { ns: 'common/base' })}
              </Typography>
            )}
          </Stack>
        </InfiniteScroll>
      </Stack>
    </Popover>
  );
};

export default NotificationList;
